/**
 * Think 命令 - 使用思考模板进行结构化思考
 */

import { Command } from 'commander';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { PromptTemplateEngine } from '../core/prompt-template-engine';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import inquirer from 'inquirer';
import ora from 'ora';
import fs from 'fs-extra';
import path from 'path';

export function createThinkCommand(): Command {
  const command = new Command('think');
  
  command
    .description('Use thinking templates for structured problem solving')
    .argument('[problem]', 'Problem or question to think about')
    .option('-t, --template <id>', 'Specify template ID', 'thinking-process')
    .option('-s, --style <style>', 'Thinking style (analytical/creative/systematic/critical)')
    .option('-c, --constraints <items>', 'Constraints (comma-separated)')
    .option('-r, --resources <items>', 'Available resources (comma-separated)')
    .option('-o, --output <path>', 'Save output to file')
    .option('-i, --interactive', 'Interactive mode')
    .option('--list', 'List available thinking templates')
    .action(withErrorHandling(async (problem: string | undefined, options) => {
      const engine = new PromptTemplateEngine();
      await engine.initialize();
      
      // 列出模板
      if (options.list) {
        await listTemplates(engine);
        return;
      }
      
      // 交互式模式
      if (options.interactive || !problem) {
        await interactiveThink(engine, options);
        return;
      }
      
      // 直接执行模式
      await directThink(engine, problem, options);
    }));
  
  return command;
}

/**
 * 列出可用的思考模板
 */
async function listTemplates(engine: PromptTemplateEngine): Promise<void> {
  const templates = engine.getTemplatesByCategory('thinking');
  
  if (templates.length === 0) {
    console.log(colors.warning('No thinking templates found.'));
    return;
  }
  
  console.log(colors.header('\n🧠 Available Thinking Templates\n'));
  
  const table = new Table({
    columns: [
      { name: 'id', title: 'Template ID', alignment: 'left' },
      { name: 'name', title: 'Name', alignment: 'left' },
      { name: 'complexity', title: 'Complexity', alignment: 'center', colored: true },
      { name: 'tags', title: 'Tags', alignment: 'left', maxLen: 30 }
    ]
  });
  
  for (const template of templates) {
    const tags = template.metadata?.tags?.join(', ') || '';
    table.addRow({
      id: template.id,
      name: template.name,
      complexity: template.metadata?.complexity?.toUpperCase() || 'NORMAL',
      tags: tags.length > 30 ? tags.substring(0, 27) + '...' : tags
    });
  }
  
  table.printTable();
  
  console.log(colors.dim('\n💡 Tips:'));
  console.log(colors.dim('• Use ') + colors.command('workflow think "problem" -t <template-id>') + colors.dim(' to use a specific template'));
  console.log(colors.dim('• Use ') + colors.command('workflow think -i') + colors.dim(' for interactive mode'));
}

/**
 * 交互式思考模式
 */
async function interactiveThink(
  engine: PromptTemplateEngine, 
  options: any
): Promise<void> {
  console.log(colors.header('\n🧠 Interactive Thinking Mode\n'));
  
  // 选择模板
  const templates = engine.getTemplatesByCategory('thinking');
  if (templates.length === 0) {
    console.log(colors.error('No thinking templates available.'));
    return;
  }
  
  const templateChoices = templates.map(t => ({
    name: `${t.name} - ${t.description}`,
    value: t.id
  }));
  
  const answers = await inquirer.prompt([
    {
      type: 'list',
      name: 'templateId',
      message: 'Select a thinking template:',
      choices: templateChoices,
      default: options.template || 'thinking-process'
    },
    {
      type: 'input',
      name: 'problem',
      message: 'What problem do you want to think about?',
      validate: (input) => input.trim().length > 0 || 'Please enter a problem'
    }
  ]);
  
  const template = engine.getTemplate(answers.templateId);
  if (!template) {
    console.log(colors.error('Template not found.'));
    return;
  }
  
  // 收集模板变量
  const variables: Record<string, any> = {
    problem: answers.problem
  };
  
  // 收集其他必需的变量
  for (const varDef of template.variables) {
    if (varDef.name === 'problem') continue; // 已经收集了
    
    if (varDef.required || !varDef.default) {
      const varAnswer = await inquirer.prompt([
        {
          type: varDef.type === 'array' ? 'input' : 
                varDef.enum ? 'list' : 'input',
          name: 'value',
          message: `${varDef.description || varDef.name}:`,
          choices: varDef.enum,
          default: varDef.default,
          filter: (input) => {
            if (varDef.type === 'array' && typeof input === 'string') {
              return input.split(',').map(s => s.trim()).filter(s => s);
            }
            return input;
          }
        }
      ]);
      
      if (varAnswer.value !== undefined && varAnswer.value !== '') {
        variables[varDef.name] = varAnswer.value;
      }
    }
  }
  
  // 渲染并显示结果
  await renderAndDisplay(engine, answers.templateId, variables, options);
}

/**
 * 直接思考模式
 */
async function directThink(
  engine: PromptTemplateEngine,
  problem: string,
  options: any
): Promise<void> {
  const templateId = options.template || 'thinking-process';
  const template = engine.getTemplate(templateId);
  
  if (!template) {
    console.log(colors.error(`Template not found: ${templateId}`));
    console.log(colors.info('Use --list to see available templates'));
    return;
  }
  
  // 准备变量
  const variables: Record<string, any> = {
    problem
  };
  
  // 处理选项
  if (options.style) {
    variables.thinkingStyle = options.style;
  }
  
  if (options.constraints) {
    variables.constraints = options.constraints.split(',').map((s: string) => s.trim());
  }
  
  if (options.resources) {
    variables.resources = options.resources.split(',').map((s: string) => s.trim());
  }
  
  await renderAndDisplay(engine, templateId, variables, options);
}

/**
 * 渲染并显示思考结果
 */
async function renderAndDisplay(
  engine: PromptTemplateEngine,
  templateId: string,
  variables: Record<string, any>,
  options: any
): Promise<void> {
  const spinner = ora('Thinking...').start();
  
  try {
    // 渲染模板
    const output = engine.render(templateId, variables);
    
    spinner.succeed('Thinking complete!');
    
    // 显示结果
    console.log(colors.header('\n📝 Thinking Output:\n'));
    console.log(output);
    
    // 保存到文件
    if (options.output) {
      const outputPath = path.resolve(options.output);
      await fs.ensureDir(path.dirname(outputPath));
      await fs.writeFile(outputPath, output, 'utf-8');
      console.log(colors.success(`\n✅ Output saved to: ${outputPath}`));
    }
    
    // 提供后续建议
    console.log(colors.header('\n🚀 Next Steps:\n'));
    console.log('1. Review the thinking output and refine if needed');
    console.log('2. Create tasks based on the analysis:');
    console.log(colors.command('   workflow task create "..."'));
    console.log('3. Use agents to implement the solution:');
    console.log(colors.command('   workflow agent developer "..."'));
    
  } catch (error: any) {
    spinner.fail('Thinking failed');
    throw error;
  }
}

/**
 * think list - 列出所有思考模板（子命令方式）
 */
export function createThinkListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('List all thinking templates')
    .alias('ls')
    .action(withErrorHandling(async () => {
      const engine = new PromptTemplateEngine();
      await engine.initialize();
      await listTemplates(engine);
    }));
  
  return command;
}

/**
 * think show - 显示模板详情
 */
export function createThinkShowCommand(): Command {
  const command = new Command('show');
  
  command
    .description('Show template details')
    .argument('<template>', 'Template ID')
    .action(withErrorHandling(async (templateId: string) => {
      const engine = new PromptTemplateEngine();
      await engine.initialize();
      
      const template = engine.getTemplate(templateId);
      if (!template) {
        console.log(colors.error(`Template not found: ${templateId}`));
        return;
      }
      
      console.log(colors.header(`\n📋 Template: ${template.name}\n`));
      console.log(colors.dim(template.description));
      console.log();
      
      // 显示变量
      if (template.variables.length > 0) {
        console.log(colors.info('Variables:'));
        const varTable = new Table({
          columns: [
            { name: 'name', title: 'Name', alignment: 'left' },
            { name: 'type', title: 'Type', alignment: 'center' },
            { name: 'required', title: 'Required', alignment: 'center' },
            { name: 'description', title: 'Description', alignment: 'left', maxLen: 40 }
          ]
        });
        
        for (const v of template.variables) {
          varTable.addRow({
            name: v.name,
            type: v.type,
            required: v.required ? '✓' : '-',
            description: v.description || ''
          });
        }
        
        varTable.printTable();
      }
      
      // 显示元数据
      if (template.metadata) {
        console.log(colors.info('\nMetadata:'));
        console.log(`• Category: ${template.metadata.category || 'N/A'}`);
        console.log(`• Complexity: ${template.metadata.complexity || 'N/A'}`);
        console.log(`• Effectiveness: ${template.metadata.effectiveness || 'N/A'}%`);
        if (template.metadata.tags) {
          console.log(`• Tags: ${template.metadata.tags.join(', ')}`);
        }
      }
      
      // 显示示例
      if (template.examples && template.examples.length > 0) {
        console.log(colors.info('\nExample Usage:'));
        console.log(colors.command(`workflow think "${template.examples[0].variables?.problem || 'your problem'}" -t ${template.id}`));
      }
    }));
  
  return command;
}